import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { resolveLoansAppRole } from './app-role';
import { refreshKeycloakSession } from './keycloak';
import { getApiBaseUrl, getAppSelectorUrl } from './runtime-urls';
import { resolveActiveTenantId } from './tenant-context';
import { useAuthStore } from '@/stores/auth-store';
import type { LoansAppRole, UserRole } from '@/types';

const API_BASE = getApiBaseUrl();
const LEGACY_TOKEN_STORAGE_KEYS = ['accessToken', 'refreshToken', 'token', 'idToken'] as const;

export const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000, // 30s request timeout
  withCredentials: true, // Send cookies with every request
});

// ── 429 Rate-limit aware retry ──────────────────────────
// When rate limited, pause all requests briefly then retry with backoff.
// This prevents the "429 storm" where TanStack Query's parallel refetches
// all hit the server simultaneously after a mutation invalidation.
let rateLimitedUntil = 0; // timestamp when we can resume requests
const MAX_429_RETRIES = 5; // Increased from 3 — SPA page loads fire 20+ parallel GETs

// Proactive throttling: when remaining drops below 10% of limit,
// voluntarily slow down to avoid hitting the wall.
let proactiveDelayMs = 0;

async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function shouldSkipSessionRefresh(config: InternalAxiosRequestConfig) {
  const url = `${config.baseURL || ''}${config.url || ''}`;
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/keycloak/exchange') ||
    url.includes('/auth/me') ||
    url.includes('/auth/logout')
  );
}

function hasHeader(
  headers: InternalAxiosRequestConfig['headers'] | Record<string, unknown> | undefined,
  headerName: string,
) {
  if (!headers) {
    return false;
  }

  const normalizedName = headerName.toLowerCase();
  if (typeof (headers as InternalAxiosRequestConfig['headers']).has === 'function') {
    return (headers as InternalAxiosRequestConfig['headers']).has(headerName);
  }

  return Object.keys(headers).some(key => key.toLowerCase() === normalizedName);
}

// Pre-flight check: if we're in a rate-limit cooldown or proactive slowdown, delay
api.interceptors.request.use(async config => {
  const now = Date.now();
  if (rateLimitedUntil > now) {
    const waitMs = rateLimitedUntil - now + Math.random() * 500; // jitter
    await sleep(waitMs);
  } else if (proactiveDelayMs > 0) {
    await sleep(proactiveDelayMs + Math.random() * 50);
  }
  return config;
});

api.interceptors.request.use(config => {
  if ((config as InternalAxiosRequestConfig & { skipTenantContext?: boolean }).skipTenantContext) {
    return config;
  }

  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser) as {
        tenantId?: string;
        appRole?: LoansAppRole;
        role?: UserRole;
        allowedTenantIds?: string[];
        availableTenants?: Array<{ id: string; name: string }>;
      };
      const activeTenantId = resolveActiveTenantId(user);
      if (activeTenantId && !hasHeader(config.headers, 'X-Tenant-Id')) {
        config.headers['X-Tenant-Id'] = activeTenantId;
      }
      if (!hasHeader(config.headers, 'X-App-Role')) {
        config.headers['X-App-Role'] = resolveLoansAppRole(user);
      }
    } catch {
      // Ignore malformed cached user payload and continue with auth header only.
    }
  }
  return config;
});

// ── Idempotency-Key for mutating requests (retries reuse same config → same key) ────
api.interceptors.request.use(config => {
  const method = (config.method || 'get').toLowerCase();
  if (!['post', 'put', 'patch', 'delete'].includes(method)) {
    return config;
  }
  const headers = (config.headers ?? {}) as Record<string, string>;
  if (headers['Idempotency-Key'] || headers['idempotency-key']) {
    return config;
  }
  const id =
    typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  headers['Idempotency-Key'] = id;
  config.headers = headers as typeof config.headers;
  return config;
});

// ── Response interceptor: handle 401 with token refresh ──
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (err: any) => void;
}> = [];

function clearAuthorizationHeader(headers: InternalAxiosRequestConfig['headers'] | undefined) {
  if (!headers) {
    return;
  }

  headers.delete?.('Authorization');
  headers.delete?.('authorization');
  delete (headers as Record<string, string>).Authorization;
  delete (headers as Record<string, string>).authorization;
}

function processQueue(error: any) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
}

function clearLocalAuthState() {
  for (const key of LEGACY_TOKEN_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  localStorage.removeItem('expiresAt');
  localStorage.removeItem('user');
  useAuthStore.setState({
    user: null,
    expiresAt: null,
    isAuthenticated: false,
  });
}

function redirectToAppSelector() {
  window.location.replace(getAppSelectorUrl());
}

api.interceptors.response.use(
  response => {
    // Proactive throttling: read X-RateLimit-Remaining header and slow down
    // when approaching the limit, instead of waiting to hit 429.
    const remaining = response.headers?.['x-ratelimit-remaining'];
    const limit = response.headers?.['x-ratelimit-limit'];
    if (remaining !== undefined && limit) {
      const pct = parseInt(remaining, 10) / parseInt(limit, 10);
      if (pct < 0.1) {
        // Below 10% remaining — add progressive delay
        proactiveDelayMs = pct < 0.05 ? 200 : 100;
      } else {
        proactiveDelayMs = 0;
      }
    }

    // Unwrap the backend's standardised { success, data, requestId } envelope
    // so every hook continues to receive the raw payload via response.data
    if (
      response.data &&
      typeof response.data === 'object' &&
      response.data.success !== undefined &&
      'data' in response.data
    ) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      skipAuthRefresh?: boolean;
    };
    const shouldSkipRefresh = shouldSkipSessionRefresh(originalRequest);

    // Only attempt refresh on 401 and if we haven't already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !shouldSkipRefresh
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => {
              clearAuthorizationHeader(originalRequest.headers);
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const session = await refreshKeycloakSession();
        useAuthStore.getState().updateSessionExpiry(session.expiresIn);
        processQueue(null);
        clearAuthorizationHeader(originalRequest.headers);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearLocalAuthState();
        redirectToAppSelector();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── 429 rate-limit retry with exponential backoff ──
    if (error.response?.status === 429) {
      const retryCount = (originalRequest as any)._retryCount ?? 0;

      if (retryCount < MAX_429_RETRIES) {
        (originalRequest as any)._retryCount = retryCount + 1;

        // Parse Retry-After header (seconds) or use exponential backoff
        const retryAfterHeader = error.response.headers?.['retry-after'];
        const retryAfterSecs = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 0;
        const backoffMs =
          retryAfterSecs > 0
            ? retryAfterSecs * 1000
            : Math.min(1000 * Math.pow(2, retryCount), 10000) + Math.random() * 1000;

        // Set global cooldown so other concurrent requests also pause
        rateLimitedUntil = Math.max(rateLimitedUntil, Date.now() + backoffMs);

        console.warn(
          `Rate limited (attempt ${retryCount + 1}/${MAX_429_RETRIES}), retrying in ${Math.round(backoffMs)}ms`,
        );
        await sleep(backoffMs);

        return api(originalRequest);
      }

      // Exhausted retries — give a user-friendly message
      console.error('Rate limit retries exhausted');
    }

    return Promise.reject(error);
  },
);
